import * as React from 'react';
import * as classnames from 'classnames';
import styled from 'styled-components';
import { ShopNowButton } from '../../internal/views/product-brief/ShopNowButton';
import { CTALink } from '../../internal/views/product-brief/CTALink';
import { observer } from 'mobx-react';
import { InventoryStatus } from '../../internal/constants/InventoryStatuses';
import { ServiceView, translate, ITranslationsCollection, config } from 'elc-service';
import { FIND_IN_STORE_BUTTON } from 'elc-service-view-names';
import {
    SHOP_NOW,
    HIDE_CTA,
    ADD_TO_COLLECTION,
    FIND_IN_STORE,
    ADD_TO_BAG,
    PREORDER
} from '../../internal/constants/CTATypes';
import { ContentSpacing } from 'elc-base-theme';
import { Sku } from '../../internal/domain/entities/Sku';
import { IAsset } from '../../internal/domain/entities/Asset';
import { ProductCTAViewController } from '../../internal/controllers/ProductCTAViewController';
import { Button } from 'elc-buttons';
import { ISizePickerImages, IShortDescriptionConfig } from '../../internal/interfaces/IProdcat';
import { AddToCollectionButton } from '../../internal/views/product-brief/AddToCollectionButton';
import { ITranslationsOverwrites } from '../product-grid-wrapper/ProductGridWrapper';
import { LimitedRemainingMessage } from '../../internal/views/common/LimitedRemainingMessage';
import { IFeatures } from '../../internal/constants/Features';
import {
    NotifyMeCTA,
    IProductNotifyMeCTA
} from '../../internal/views/product-notify-me/NotifyMeCTA';
import { formatDateFromString } from '../../internal/utils/DateFormat';
import {
    ADD_TO_BAG_BUTTON,
    PREORDER_BUTTON,
    PREORDER_BUTTON_DISABLED
} from '../../internal/constants/DataTestIdAttributes';
import { classes } from '../../internal/utils/Classes';

export interface IMapping {
    [key: string]: string;
}

export interface IStickyButton {
    isProductShaded?: boolean;
}

export interface IWaitlist {
    enabled?: boolean;
    enabledByStatus?: string[];
}

export interface IProductCTAConfig {
    selectedSku?: Sku;
    skuId: string;
    quantity?: number;
    replenishment?: number;
    enableProductDetailLink?: boolean;
    enableNotifyMe?: boolean;
    waitlist?: IWaitlist;
    ctaType?: string;
    enableQuickView?: boolean;
    enableMobileQuickView?: boolean;
    enableQuickViewShortcut?: boolean;
    hideProductRating?: boolean;
    productGridHideReadReviewsLink?: boolean;
    enableLearnMoreLink?: boolean;
    compliance?: { gdpr: boolean };
    showSuccessLabel?: boolean;
    hasStickyButtonVariation?: boolean;
    disabled?: boolean;
    closeModalAction?(): void;
    largeImages?: IAsset[];
    autoReplenishOptions: number[];
    customAddToBagLabel?: string;
    className?: string;
    addToBagWrapperClassName?: string;
    addToBagDataTestId?: string;
    addToCollectionDataTestId?: string;
    isUpdatedInventorySource?: boolean;
    sizePickerImages?: ISizePickerImages;
    hideProductPricePerUnit?: boolean;
    showProductQuantitySelector?: boolean;
    shortDescriptionLocation?: IShortDescriptionConfig;
    showPriceOnStickyAddToBag?: boolean;
    isDisplayedOnSticky?: boolean;
    enableAutoReplenishment?: boolean;
    translationsOverwrites?: ITranslationsOverwrites;
    positionIndex?: number;
    features?: IFeatures;
    isCoreSite?: boolean;
}

export interface IProductCTA {
    config: IProductCTAConfig;
    translations?: ITranslationsCollection;
    viewController: ProductCTAViewController;
}

export interface IProductCTAProps {
    config: IProductCTAConfig;
    translations?: ITranslationsCollection;
}

export interface IProductAddToBag {
    config: {
        skuId: string;
        inventoryStatus: string;
        customAddToBagLabel?: string;
        addToBagDataTestId?: string;
        disabled?: boolean;
        quantity?: number;
        showSuccessLabel?: boolean;
        replenishment?: number;
        isProductShaded?: boolean;
        hasStickyButtonVariation?: boolean;
        showPriceOnStickyAddToBag?: boolean;
    };
    className?: string;
    addToBagWrapperClassName?: string;
    translations?: ITranslationsCollection;
    closeModalAction?(): void;
}

export interface IProductFullAddToBagButtonConfig {
    enableNotifyMe?: boolean;
    waitlist?: IWaitlist;
    complianceGdpr?: boolean;
}

export interface IProductFullAddToBagButton {
    isUpdatedInventorySource?: boolean;
    config: IProductFullAddToBagButtonConfig;
    quantity?: number;
    showSuccessLabel?: boolean;
    translations?: ITranslationsCollection;
    className?: string;
    customAddToBagLabel?: string;
    disabled?: boolean;
    addToBagWrapperClassName?: string;
    autoReplenishOptions: number[];
    selectedSku: Sku;
}

const CTAButtonWrapper = styled.div`
    width: 100%;
`;

const StyledCTALink = styled(CTALink)`
    margin-top: ${ContentSpacing.space8};
`;

const StyledButton = styled(Button)`
    width: 100%;
    border: none;
    padding: 0 ${ContentSpacing.space16};
    overflow-x: hidden;
    cursor: pointer;
`;

export const enableByInventoryStatus = (enabledByStatus: string[], inventoryStatus: string) => {
    return (
        enabledByStatus.includes(inventoryStatus) ||
        (enabledByStatus.length === 0 && inventoryStatus !== InventoryStatus.SoldOut)
    );
};

@config(['waitlist', 'features', 'isCoreSite'])
@translate([
    'shopNowButtonLabel',
    'ctaLinkLabel',
    'addToBagButtonLabel',
    'outOfStockButtonLabel',
    'soldOutButtonLabel',
    'comingSoonButtonLabel',
    'addedToCartLabel',
    'availableOn',
    'preOrder'
])
@observer
class ProductCTA extends React.Component<IProductCTA> {
    public static displayName: string;

    constructor(props: IProductCTA) {
        super(props);
        props.viewController.initialize(props.config);
    }

    public componentWillReceiveProps(props: IProductCTA) {
        props.viewController.initialize(props.config);
    }

    private preorderAvailabilityLabel = (preorderMessage: string, preorderDate: string) => {
        const formattedDate = formatDateFromString(preorderDate);
        const { day, month } = formattedDate;

        return preorderMessage.replace(/::PREORDER_DATE::/gi, `${day}/${month}`);
    };

    private navigateToProduct = async () => {
        const { positionIndex } = this.props.config;
        await this.props.viewController.navigateToProduct(positionIndex as number);
    };

    private onAddToBagClick = async () => {
        const { skuId, quantity, replenishment, showSuccessLabel } = this.props.config;
        const appliedShowSuccessLabel = showSuccessLabel !== undefined ? showSuccessLabel : true;

        await this.props.viewController.addToBag(
            skuId,
            quantity,
            appliedShowSuccessLabel,
            replenishment
        );

        if (this.props.config.closeModalAction) {
            setTimeout(this.props.config.closeModalAction, 500);
        }
    };

    private onAddToCollectionButtonClick = async () => {
        const { quantity, skuId } = this.props.config;

        await this.props.viewController.addSkuToCollection(skuId, quantity);
    };

    private getAddToBagButtonLabels(): IMapping {
        const { customAddToBagLabel } = this.props.config;
        const {
            addToBagButtonLabel,
            outOfStockButtonLabel,
            soldOutButtonLabel,
            comingSoonButtonLabel,
            preOrder
        } = this.props.translations as ITranslationsCollection;

        return {
            [InventoryStatus.Active]: customAddToBagLabel
                ? customAddToBagLabel
                : addToBagButtonLabel,
            [InventoryStatus.TempOutOfStock]: outOfStockButtonLabel,
            [InventoryStatus.ComingSoon]: comingSoonButtonLabel,
            [InventoryStatus.SoldOut]: soldOutButtonLabel,
            [InventoryStatus.FreePromo]: customAddToBagLabel
                ? customAddToBagLabel
                : addToBagButtonLabel,
            [InventoryStatus.PreOrder]: preOrder
        };
    }

    private getAddToBagButton() {
        const { config: buttonConfig, viewController, translations } = this.props;
        const selectedSku: Sku = buttonConfig.selectedSku || viewController.data.selectedSku;

        const {
            quantity,
            replenishment,
            hasStickyButtonVariation,
            showSuccessLabel,
            customAddToBagLabel,
            disabled,
            addToBagDataTestId,
            isDisplayedOnSticky,
            showPriceOnStickyAddToBag,
            translationsOverwrites,
            skuId
        } = buttonConfig;

        const {
            inventoryStatus,
            shades,
            preorderDate,
            isPreorderable,
            isPreorderEnabled,
            isShoppable
        } = selectedSku;

        const isProductShaded = shades.length > 0;

        const { addedToCartLabel, availableOn } = translations as ITranslationsCollection;

        const inventoryConfig = {
            skuId,
            inventoryStatus,
            quantity,
            replenishment,
            isProductShaded,
            hasStickyButtonVariation,
            showSuccessLabel,
            customAddToBagLabel,
            disabled,
            addToBagDataTestId
        };

        const { isShowingSuccessLabel, addingToCart, disableAddToCart } = viewController.data;

        const addToBagButtonLabels = this.getAddToBagButtonLabels();
        const isPreorderDisabled = isPreorderEnabled && !isShoppable;
        const isButtonDisabled =
            !selectedSku.isShoppable || addingToCart || disableAddToCart || isPreorderDisabled;
        const isDisabled = disabled || isButtonDisabled;

        const addToBagButtonClassNames = classnames({
            'elc-add-to-bag-button': true,
            'js-add-to-bag-button': true,
            'elc-add-to-bag-button-disabled': isDisabled,
            'js-add-to-bag-button-disabled': isDisabled,
            'elc-preorder-button': isPreorderable,
            'js-preorder-button': isPreorderable,
            'elc-preorder-button-disabled': isPreorderDisabled,
            'js-preorder-button-disabled': isPreorderDisabled
        });

        const addToBagButtonDataTestId =
            addToBagDataTestId ||
            (isPreorderDisabled && PREORDER_BUTTON_DISABLED) ||
            (isPreorderable && PREORDER_BUTTON) ||
            ADD_TO_BAG_BUTTON;

        const CTAButtonLabel = isPreorderDisabled
            ? this.preorderAvailabilityLabel(availableOn, preorderDate)
            : addToBagButtonLabels[inventoryStatus];
        const productCTAButtonLabel = isShowingSuccessLabel ? addedToCartLabel : CTAButtonLabel;

        let processedCtaLabel =
            translationsOverwrites && translationsOverwrites.ctaButtonLabel
                ? translationsOverwrites.ctaButtonLabel
                : productCTAButtonLabel;
        if (isDisplayedOnSticky && showPriceOnStickyAddToBag && !isPreorderDisabled) {
            const priceObject = selectedSku.priceObject;
            if (!!priceObject) {
                processedCtaLabel = `${processedCtaLabel} - ${priceObject.priceFormatted}`;
            }
        }

        return (
            <div className={this.props.config.className}>
                <StyledButton
                    className={addToBagButtonClassNames}
                    onClick={this.onAddToBagClick}
                    disabled={isDisabled}
                    aria-label={productCTAButtonLabel}
                    title={productCTAButtonLabel}
                    data-test-id={addToBagButtonDataTestId}
                    data-qty={inventoryConfig.quantity}
                >
                    {processedCtaLabel}
                </StyledButton>
            </div>
        );
    }

    private getAddToCollectionButton() {
        const selectedSku = this.props.viewController.data.selectedSku;
        const {
            disabled,
            addToCollectionDataTestId,
            translationsOverwrites,
            className
        } = this.props.config;

        const { inventoryStatus } = selectedSku;

        const {
            addToCollectionButtonLabel,
            outOfStockButtonLabel,
            soldOutButtonLabel,
            comingSoonButtonLabel
        } = this.props.translations as ITranslationsCollection;

        const customCtaLabel = translationsOverwrites && translationsOverwrites.ctaButtonLabel;
        const addToCollectionButtonLabels: IMapping = {
            [InventoryStatus.Active]: customCtaLabel ? customCtaLabel : addToCollectionButtonLabel,
            [InventoryStatus.TempOutOfStock]: outOfStockButtonLabel,
            [InventoryStatus.ComingSoon]: comingSoonButtonLabel,
            [InventoryStatus.SoldOut]: soldOutButtonLabel,
            [InventoryStatus.FreePromo]: customCtaLabel
                ? customCtaLabel
                : addToCollectionButtonLabel
        };
        const initialStateLabel = addToCollectionButtonLabels[inventoryStatus];

        const isButtonDisabled = !selectedSku.isShoppable;
        const isDisabled = disabled || isButtonDisabled;

        const clickedStateLabel =
            translationsOverwrites && translationsOverwrites.pressedCtaButtonLabel;

        const addToCollectionConfig = {
            isDisabled,
            addToCollectionDataTestId,
            initialStateLabel,
            clickedStateLabel
        };

        return (
            <div className={className}>
                <AddToCollectionButton
                    config={addToCollectionConfig}
                    onAddToCollectionClick={this.onAddToCollectionButtonClick}
                />
            </div>
        );
    }

    private getFindInStoreButton() {
        const findInStoreClassNames = classes('find-in-store-button');
        const selectedSkuId = this.props.config.skuId;

        return (
            <div className={findInStoreClassNames}>
                <ServiceView name={FIND_IN_STORE_BUTTON} config={{ skuId: selectedSkuId }} />
            </div>
        );
    }

    private getCTAType() {
        const { ctaType, isCoreSite } = this.props.config;

        return isCoreSite &&
            (ctaType === ADD_TO_COLLECTION ||
                ctaType === ADD_TO_BAG ||
                typeof ctaType === 'undefined')
            ? FIND_IN_STORE
            : ctaType;
    }

    private getCTAButton() {
        const { config: ctaConfig, translations, viewController } = this.props;
        const { shopNowButtonLabel } = translations as ITranslationsCollection;
        const { enableNotifyMe, waitlist, translationsOverwrites } = ctaConfig;
        const selectedSku = ctaConfig.selectedSku || viewController.data.selectedSku;

        const ctaButtonType = this.getCTAType();
        const { inventoryStatus, partialParentProduct } = selectedSku;

        const { productUrl } = partialParentProduct;

        const customCtaLabel = translationsOverwrites && translationsOverwrites.ctaButtonLabel;

        const shopNowLabel = customCtaLabel ? customCtaLabel : shopNowButtonLabel;

        const isProductAvailable = inventoryStatus === InventoryStatus.Active;
        const enabledByStatus =
            waitlist && waitlist.enabledByStatus ? waitlist.enabledByStatus : [];

        const notifyMeEnabled = enableNotifyMe || (waitlist && waitlist.enabled);

        const showNotifyMeButton =
            notifyMeEnabled &&
            !isProductAvailable &&
            enableByInventoryStatus(enabledByStatus, inventoryStatus);

        const CTAButtonWrapperClassNames = classnames({
            'elc-product-cta-button-wrapper': true,
            'js-product-cta-button-wrapper': true
        });

        let ctaButton: JSX.Element | null;

        switch (ctaButtonType) {
            case HIDE_CTA:
                ctaButton = null;
                break;
            case SHOP_NOW:
                ctaButton = (
                    <ShopNowButton
                        label={shopNowLabel}
                        productUrl={productUrl}
                        onShopNowClick={this.navigateToProduct}
                    />
                );
                break;
            case ADD_TO_COLLECTION:
                ctaButton = showNotifyMeButton
                    ? this.getNotifyMeCTA()
                    : this.getAddToCollectionButton();
                break;
            case PREORDER:
                ctaButton = this.getAddToBagButton();
                break;
            case FIND_IN_STORE:
                ctaButton = this.getFindInStoreButton();
                break;
            default:
                // ADD_TO_BAG
                // if this gets refactored and the default will no longer come from here, make sure to update the inv status conditions across all files
                ctaButton = showNotifyMeButton ? this.getNotifyMeCTA() : this.getAddToBagButton();
        }

        return (
            <CTAButtonWrapper className={CTAButtonWrapperClassNames}>{ctaButton}</CTAButtonWrapper>
        );
    }

    private getNotifyMeCTA() {
        const { enableNotifyMe, waitlist, compliance } = this.props.config;
        const selectedSku =
            this.props.config.selectedSku || this.props.viewController.data.selectedSku;

        const notifyMeCTAProps: IProductNotifyMeCTA = {
            selectedSku: selectedSku,
            config: {
                enableNotifyMe,
                waitlist,
                compliance
            }
        };

        return <NotifyMeCTA {...notifyMeCTAProps} />;
    }

    private getCTALink() {
        const selectedSku = this.props.viewController.data.selectedSku;
        const { translations } = this.props;

        const { ctaLinkLabel } = translations as ITranslationsCollection;

        const { productUrl } = selectedSku.partialParentProduct;

        return (
            <StyledCTALink
                onCTALinkClick={this.navigateToProduct}
                label={ctaLinkLabel}
                productUrl={productUrl}
            />
        );
    }

    private getLimitedRemainingMessage() {
        const { highVelocityQuantity = 0 } = this.props.viewController.data.selectedSku;

        return (
            highVelocityQuantity > 0 && (
                <LimitedRemainingMessage skuCountRemaining={highVelocityQuantity} />
            )
        );
    }

    public render() {
        const { enableProductDetailLink, ctaType, features } = this.props.config;
        const enableLimitedRemaining =
            features && features.limitedRemaining && features.limitedRemaining.enabled;

        const productCTAWrapperClassNames = classnames({
            'elc-product-cta-wrapper': true,
            'js-product-cta-wrapper': true
        });

        const displayCTAButton = ctaType !== HIDE_CTA;

        return (
            <div className={productCTAWrapperClassNames}>
                {displayCTAButton && this.getCTAButton()}
                {enableProductDetailLink && this.getCTALink()}
                {enableLimitedRemaining && this.getLimitedRemainingMessage()}
            </div>
        );
    }
}

ProductCTA.displayName = 'ProductCTA';

export default ProductCTA;
